import { Dispatch, SetStateAction, useState } from 'react';
import { useIntl } from 'react-intl';
import { DateTimeRangePicker } from '@rossum/components/date-time-range-picker';
import { SelectedDates } from '@rossum/components/date-time-range-picker/types';
import { IconCalendarEvent } from '@rossum/ui/icons/tabler';
import {
  Box,
  Button,
  Paper,
  Popover,
  Stack,
  SvgIcon,
  TextField,
} from '@rossum/ui/material';
import { formatDateQuery } from '../../../redux/modules/statistics/helpers';

type StatisticsDatePickerProps = {
  selectedDates: SelectedDates;
  setSelectedDates: Dispatch<SetStateAction<SelectedDates>>;
  dateLabel: string;
  onCancel: () => void;
  onConfirm: (params: { beginDate: string; endDate: string }) => void;
};
export const StatisticsDatePicker = ({
  selectedDates,
  setSelectedDates,
  dateLabel,
  onCancel,
  onConfirm,
}: StatisticsDatePickerProps) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const handleReset = () => {
    setAnchorEl(null);
    onCancel();
  };

  return (
    <Box sx={{ width: 250, flexShrink: 0 }}>
      <TextField
        data-cy="statistics-date-picker"
        autoComplete="off"
        onClick={e => setAnchorEl(e.currentTarget)}
        value={dateLabel}
        size="small"
        fullWidth
        slotProps={{
          input: {
            readOnly: true,
            startAdornment: (
              <SvgIcon fontSize="medium" sx={{ mr: 1 }}>
                <IconCalendarEvent />
              </SvgIcon>
            ),
          },
        }}
      />
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleReset}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack component={Paper} sx={{ p: 2, gap: 2 }}>
          <DateTimeRangePicker
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            operatorValue="is"
            hideTimePickers
            locale={intl.locale}
          />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              data-cy="statistics-date-picker-confirm-button"
              onClick={() => {
                if (selectedDates[0] && selectedDates[1]) {
                  onConfirm({
                    beginDate: formatDateQuery(selectedDates[0]),
                    endDate: formatDateQuery(selectedDates[1]),
                  });
                  handleReset();
                }
              }}
            >
              Confirm
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Box>
  );
};
